import React from "react";
import { Box, FormControl, Option, Grid, Divider } from "@mui/joy";
import ActionTooltip from "../../../common/ActionTooltip";
import DarkSelect from "../../../common/DarkSelect";
import type { Module, Criteria } from "../../../../api/followup/Followup.typed";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { SAddCriteria, SDelete, AddCircleIcon as AddCircle } from "../../../../styles/StyledMUI";
import DarkInput from "../../../common/DarkInput";
import DarkCard from "../../../common/DarkCard";
import IconButtonCTA from "../../../common/IconButtonCTA";
import { colorTokens } from "../../../../styles/globalStyles";

interface ModuleFormProps {
  module: Module;
  onChange: (updatedModule: Module) => void;
  onDeleteModule: () => void;
}

export default function ModuleForm({ module, onChange, onDeleteModule }: ModuleFormProps) {
  const [savedCustomOptions, setSavedCustomOptions] = React.useState<string[][]>([]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...module, name: e.target.value });
  };

  const handleCriteriaChange = (index: number, updatedCriteria: Criteria) => {
    const updatedCriteriaList = [...module.criteria];
    updatedCriteriaList[index] = updatedCriteria;
    onChange({ ...module, criteria: updatedCriteriaList });
  };

  const handleOptionsTypeChange = (index: number, newValue: string | null) => {
    const updatedCriteria = [...module.criteria];
    if (newValue === "default") {
      if (!savedCustomOptions[index]) {
        savedCustomOptions[index] = [...updatedCriteria[index].options];
        setSavedCustomOptions([...savedCustomOptions]);
      }
      updatedCriteria[index].options = ["OK", "A retravailler"];
    } else if (newValue === "custom") {
      if (savedCustomOptions[index]) {
        updatedCriteria[index].options = [...savedCustomOptions[index]];
      } else {
        updatedCriteria[index].options = [""];
      }
    }
    onChange({ ...module, criteria: updatedCriteria });
  };

  const addOption = (index: number) => {
    const updatedCriteria = [...module.criteria];
    updatedCriteria[index].options.push("");
    onChange({ ...module, criteria: updatedCriteria });
  };

  const deleteOption = (criteriaIndex: number, optionIndex: number) => {
    const updatedCriteria = [...module.criteria];
    updatedCriteria[criteriaIndex].options.splice(optionIndex, 1);
    onChange({ ...module, criteria: updatedCriteria });
  };

  const handleOptionChange = (criteriaIndex: number, optionIndex: number, value: string) => {
    const updatedCriteria = [...module.criteria];
    updatedCriteria[criteriaIndex].options[optionIndex] = value;
    onChange({ ...module, criteria: updatedCriteria });
  };

  const handleDeleteCriteria = (index: number) => {
    const updatedCriteriaList = [...module.criteria];
    updatedCriteriaList.splice(index, 1);
    onChange({ ...module, criteria: updatedCriteriaList });
  };

  const addCriteria = () => {
    onChange({
      ...module,
      criteria: [
        ...module.criteria,
        { description: "", options: ["OK", "A retravailler"], comments: true },
      ],
    });
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const updatedCriteriaList = Array.from(module.criteria);
    const [reorderedItem] = updatedCriteriaList.splice(result.source.index, 1);
    updatedCriteriaList.splice(result.destination.index, 0, reorderedItem);

    onChange({ ...module, criteria: updatedCriteriaList });
  };

  return (
    <DarkCard>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <DarkInput
          placeholder="Nom du Module"
          value={module.name}
          onChange={handleNameChange}
          fullWidth
          sx={{ marginBottom: 2 }}
        />
      </Box>

      <Box sx={{ maxWidth: "80vw", overflowY: "auto" }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="criteria">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {module.criteria.map((criteria: Criteria, index: number) => (
                  <Draggable key={index} draggableId={`criteria-${index}`} index={index}>
                    {(provided) => (
                      <DarkCard
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{ marginBottom: 2 }}
                      >
                        <Grid container alignItems="center" spacing={2}>
                          <Grid xs={10}>
                            <DarkInput
                              placeholder="Description du Critère"
                              value={criteria.description}
                              onChange={(e) =>
                                handleCriteriaChange(index, {
                                  ...criteria,
                                  description: e.target.value,
                                })
                              }
                              fullWidth
                              sx={{ marginBottom: 2 }}
                            />
                            <FormControl>
                              <DarkSelect
                                value={
                                  criteria.options[0] === "OK" &&
                                  criteria.options[1] === "A retravailler"
                                    ? "default"
                                    : "custom"
                                }
                                onChange={(e, newValue) => handleOptionsTypeChange(index, newValue)}
                                placeholder="Options"
                                sx={{ marginBottom: 2 }}
                              >
                                <Option value="default">Par défaut: "OK / A retravailler"</Option>
                                <Option value="custom">Options configurables</Option>
                              </DarkSelect>
                            </FormControl>

                            {criteria.options[0] !== "OK" && (
                              <Grid container spacing={2} alignItems="center" textAlign="center">
                                {criteria.options.map((option: string, optIndex: number) => (
                                  <React.Fragment key={optIndex}>
                                    <Grid xs={2}>
                                      <DarkInput
                                        placeholder={`Option ${optIndex + 1}`}
                                        value={option}
                                        onChange={(e) =>
                                          handleOptionChange(index, optIndex, e.target.value)
                                        }
                                        fullWidth
                                      />
                                    </Grid>
                                    <Grid xs={1}>
                                      <ActionTooltip title="Supprimer option">
                                        <IconButtonCTA
                                          ctaVariant="danger"
                                          aria-label="Supprimer option"
                                          onClick={() => deleteOption(index, optIndex)}
                                        >
                                          <SDelete />
                                        </IconButtonCTA>
                                      </ActionTooltip>
                                    </Grid>
                                  </React.Fragment>
                                ))}
                                <Grid xs={1}>
                                  <ActionTooltip title="Ajouter option">
                                    <AddCircle
                                      sx={{
                                        cursor: "pointer",
                                        color: colorTokens.accent.primary,
                                      }}
                                      onClick={() => addOption(index)}
                                    />
                                  </ActionTooltip>
                                </Grid>
                              </Grid>
                            )}
                          </Grid>

                          <Box sx={{ position: "absolute", bottom: 10, right: 10 }}>
                            <ActionTooltip title="Supprimer critère">
                              <IconButtonCTA
                                ctaVariant="danger"
                                aria-label="Supprimer critère"
                                onClick={() => handleDeleteCriteria(index)}
                              >
                                <SDelete />
                              </IconButtonCTA>
                            </ActionTooltip>
                          </Box>
                        </Grid>
                      </DarkCard>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>

      <Divider sx={{ marginBottom: 2 }} />
      <Box sx={{ display: "flex" }}>
        <ActionTooltip title="Supprimer Module">
          <IconButtonCTA
            ctaVariant="danger"
            aria-label="Supprimer module"
            sx={{ marginRight: 2 }}
            onClick={onDeleteModule}
          >
            <SDelete />
          </IconButtonCTA>
        </ActionTooltip>
        <ActionTooltip title="Ajouter critère">
          <IconButtonCTA ctaVariant="accent" aria-label="Ajouter critère" onClick={addCriteria}>
            <SAddCriteria />
          </IconButtonCTA>
        </ActionTooltip>
      </Box>
    </DarkCard>
  );
}
